import React, { useState, useEffect } from 'react';
import {
  Upload, Loader, AlertCircle, CheckCircle, AlertTriangle, Shield,
  FileText, Download, Send, Sparkles, ChevronDown, ChevronUp, Zap, ExternalLink,
  Gavel
} from 'lucide-react';
import PageHeader from './PageHeader';
import './ContractCompliance.css';
import './AnalysisLayout.css';


const FYP2_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const SEV_COLOR = { HIGH: '#E74C3C', MEDIUM: '#E67E22', LOW: '#27AE60' };
const SEV_CLASS = { HIGH: 'risk-high', MEDIUM: 'risk-medium', LOW: 'risk-low' };
const CONST_COLOR = '#6C3483'; // Purple for constitutional violations
const LABEL_COLOR = {
  INDEMNIFICATION: '#E74C3C', TERMINATION: '#E67E22', PAYMENT: '#27AE60',
  CONFIDENTIALITY: '#3498DB', LIABILITY: '#9B59B6', GOVERNING_LAW: '#1ABC9C',
  INTELLECTUAL_PROPERTY: '#E67E22', FORCE_MAJEURE: '#34495E', AMENDMENT: '#16A085', OTHER: '#95A5A6',
};

// ─── Sanitization and truncation helpers ────────────────────────────────────
function sanitizeSensitiveData(text) {
  if (!text) return '';
  const patterns = [
    /\b\d{5}-\d{7}-\d{1}\b/g, // CNIC
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Email
    /(?:\+92|0)\d{10}\b/g, // Phone
    /\b\d{13}\b/g, // CNIC raw
    /\b[A-Z]{2}\d{7}\b/g, // Passport
    /PK\d{2}\s?\d{4}\s?\d{4}\s?\d{4}\s?\d{4}/gi, // IBAN
  ];
  let sanitized = text;
  patterns.forEach(p => { sanitized = sanitized.replace(p, '[SENSITIVE_DATA_REMOVED]'); });
  return sanitized;
};

function smartTruncate(text, limit = 200) {
  if (!text || text.length <= limit) return text;

  // Regex to find the last sentence end (., !, ?) followed by space or end of string
  const sub = text.substring(0, limit);
  const matches = [...sub.matchAll(/[.!?](\s|$)/g)];

  if (matches.length > 0) {
    const lastMatch = matches[matches.length - 1];
    return text.substring(0, lastMatch.index + 1);
  }

  // Fallback to word boundary if no sentence end found
  const lastSpace = sub.lastIndexOf(' ');
  if (lastSpace > limit * 0.7) {
    return sub.substring(0, lastSpace) + '...';
  }
  return sub.substring(0, limit) + '...';
}

const STAGES = [
  'Parsing PDF…', 'Summarizing contract…', 'Extracting clauses…',
  'Classifying clauses…', 'Detecting risks…', 'Matching advocates…',
];

// ─── Compliance score ring ────────────────────────────────────────────────────
function ScoreRing({ score }) {
  const r = 60, circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#2ECC71' : score >= 50 ? '#F4C542' : '#E74C3C';
  const label = score >= 80 ? 'Compliant' : score >= 50 ? 'Moderate' : 'High Risk';
  return (
    <div className="circular-progress">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#E3F2FD" strokeWidth="12" />
        <circle cx="80" cy="80" r={r} fill="none" stroke={color} strokeWidth="12"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" className="progress-ring" />
      </svg>
      <div className="progress-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <span className="progress-value" style={{ fontSize: '32px', fontWeight: '800', lineHeight: '1.1', color: '#1A237E' }}>{Math.round(score)}%</span>
        <span className="progress-label" style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
    </div>
  );
}

// ─── Single expandable clause card ───────────────────────────────────────────
function ClauseCard({ clause }) {
  const [open, setOpen] = useState(false);
  const risks = clause.risks || [];
  const topSev = risks.find(r => r.severity === 'HIGH') ? 'HIGH'
    : risks.find(r => r.severity === 'MEDIUM') ? 'MEDIUM'
      : risks.length ? 'LOW' : null;
  const labelColor = LABEL_COLOR[clause.classification?.label] || '#95A5A6';

  return (
    <div className={`issue-card-glass ${topSev ? SEV_CLASS[topSev] : ''}`}
      style={{ marginBottom: 12 }}>

      {/* Header row */}
      <div className="issue-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(v => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            background: labelColor, color: '#fff',
            padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
          }}>
            {clause.classification?.label || 'OTHER'}
          </span>
          {topSev && <span className={`risk-badge ${SEV_CLASS[topSev]}`}>{topSev} Risk</span>}
          <span style={{ fontSize: 12, color: '#888' }}>
            {((clause.classification?.confidence || 0) * 100).toFixed(0)}% confidence
            {clause.classification?.needs_review && ' · ⚠ review'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {risks.length > 0 && (
            <span style={{ fontSize: 12, color: SEV_COLOR[topSev], fontWeight: 600 }}>
              {risks.length} risk{risks.length > 1 ? 's' : ''}
            </span>
          )}
          <span style={{ fontSize: 12, color: '#aaa' }}>#{clause.clause_index + 1}</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {/* Clause text */}
      <div className="issue-content">
        {clause.heading && (
          <div className="issue-row">
            <span className="issue-label">Heading:</span>
            <span className="issue-value" style={{ fontWeight: 600 }}>{sanitizeSensitiveData(clause.heading)}</span>
          </div>
        )}
        <div className="issue-row">
          <span className="issue-label">Text:</span>
          <span className="issue-value">
            {open ? sanitizeSensitiveData(clause.text) : sanitizeSensitiveData(smartTruncate(clause.text, 200))}
          </span>
        </div>
      </div>

      {/* Risk details — only when expanded */}
      {open && risks.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {risks.map((risk, ri) => (
            <div key={ri} style={{
              background: `${SEV_COLOR[risk.severity]}12`,
              border: `1px solid ${SEV_COLOR[risk.severity]}40`,
              borderRadius: 8, padding: '10px 14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span className={`risk-badge ${SEV_CLASS[risk.severity]}`}>{risk.severity}</span>
                <strong style={{ fontSize: 13 }}>{risk.risk_type.replace(/_/g, ' ')}</strong>
                <span style={{ fontSize: 11, color: '#aaa', marginLeft: 'auto' }}>{risk.risk_id}</span>
              </div>
              <p style={{ fontSize: 12, color: '#555', margin: '4px 0' }}>
                <strong>Trigger:</strong> <em>"{sanitizeSensitiveData(risk.trigger_phrase)}"</em>
              </p>
              <p style={{ fontSize: 12, color: '#555', margin: '4px 0' }}>
                <strong>Why:</strong> {sanitizeSensitiveData(risk.explanation)}
              </p>
              <p style={{ fontSize: 12, color: '#27AE60', margin: '4px 0' }}>
                <strong>Fix:</strong> {sanitizeSensitiveData(risk.recommendation)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContractCompliance({ onNavigate }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const STAGES = [
    'Parsing PDF Structure',
    'Summarizing Executive Intent',
    'Extracting Legal Clauses',
    'Classifying Provisions',
    'Scanning Risk Liabilities',
    'Matching Specialist Advocates',
    'Finalizing Report'
  ];

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are accepted.'); return;
    }
    if (f.size > 10 * 1024 * 1024) { setError('Max 10 MB.'); return; }
    setFile(f); setError(null); setResult(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setStageIdx(0);
    setProgress(5);
    setStatusMessage('Initializing analysis...');

    try {
      const fd = new FormData();
      fd.append('file', file);

      const initRes = await fetch(`${FYP2_BASE}/api/v1/analyze/upload`, { method: 'POST', body: fd });
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${initRes.status}`);
      }

      const { task_id } = await initRes.json();

      const poll = async () => {
        try {
          const statusRes = await fetch(`${FYP2_BASE}/api/v1/analyze/status/${task_id}`);
          if (!statusRes.ok) return;

          const data = await statusRes.json();
          if (data.status === 'completed') {
            setLoading(false);
            const dataResult = data.result || {};
            const sm = dataResult.summary || {};
            const high = sm.high_risk_count || 0;
            const med = sm.medium_risk_count || 0;
            const low = sm.low_risk_count || 0;
            const score = Math.max(0, Math.min(100, 100 - high * 15 - med * 8 - low * 3));

            const finalResult = {
              fileName: file.name,
              analysisId: dataResult.analysis_id,
              executiveSummary: dataResult.contract_summary?.executive_summary || '',
              clauses: dataResult.clauses || [],
              totalClauses: sm.total_clauses || 0,
              riskCount: sm.risk_count || 0,
              highCount: high, medCount: med, lowCount: low,
              overallLevel: sm.overall_risk_level || 'NONE',
              complianceScore: score,
              advocateRecs: dataResult.recommended_advocates || [],
              inferenceMs: dataResult.metadata?.inference_time_ms || 0,
            };

            setResult(finalResult);
            setFile(null);
          } else if (data.status === 'failed') {
            throw new Error(data.error || 'Analysis failed on server');
          } else {
            setProgress(data.progress || 5);
            setStageIdx(data.stage || 0);
            setStatusMessage(data.message || STAGES[data.stage] || 'Processing...');
            setTimeout(poll, 1500);
          }
        } catch (err) {
          setLoading(false);
          setError(err.message);
        }
      };

      setTimeout(poll, 1000);

    } catch (err) {
      setLoading(false);
      setError(err.message || 'Analysis failed. Please check connection.');
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const riskyClauses = result.clauses.filter(c => c.risks?.length > 0);
    const lines = [
      'CONTRACT COMPLIANCE REPORT', '='.repeat(42),
      `File      : ${result.fileName}`,
      `Generated : ${new Date().toLocaleString()}`,
      `ID        : ${result.analysisId}`,
      '',
      `Score     : ${result.complianceScore}%  (${result.overallLevel} RISK)`,
      `Clauses   : ${result.totalClauses}   Risks: ${result.riskCount}  `
      + `(H:${result.highCount} M:${result.medCount} L:${result.lowCount})`,
      '',
      'EXECUTIVE SUMMARY', '-'.repeat(40), sanitizeSensitiveData(result.executiveSummary), '',
      'RISK DETAILS', '-'.repeat(40),
      ...riskyClauses.flatMap(c => [
        `Clause #${c.clause_index + 1} [${c.classification?.label}] ${sanitizeSensitiveData(c.heading || '')}`,
        ...c.risks.map(r => `  [${r.severity}] ${r.risk_type}: ${sanitizeSensitiveData(r.explanation)}`),
        `  Fix: ${c.risks.map(r => sanitizeSensitiveData(r.recommendation)).join(' | ')}`,
        '',
      ]),
      '='.repeat(42),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([lines], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredClauses = (result?.clauses || []).filter(c => {
    if (activeTab === 'overview' || activeTab === 'all') return true;
    const tabToSev = { 'high': 'HIGH', 'medium': 'MEDIUM', 'low': 'LOW' };
    const requiredSev = tabToSev[activeTab];
    const risks = c.risks || [];
    const topSev = risks.find(r => r.severity === 'HIGH') ? 'HIGH'
      : risks.find(r => r.severity === 'MEDIUM') ? 'MEDIUM'
        : risks.length ? 'LOW' : null;
    return topSev === requiredSev;
  });

  const levelBg = { HIGH: '#E74C3C', MEDIUM: '#E67E22', LOW: '#27AE60', NONE: '#27AE60' };

  return (
    <div className="analysis-page-container">
      <div className="analysis-hero">
        <div className="hero-text-content">
          <h1>Contract Risk Assessment</h1>
          <p>
            Securely upload legal documents for automated risk assessment, compliance
            mapping, and executive summarization using our enterprise-grade AI.
          </p>
        </div>
        <div className="hero-abstract-decoration">
          <div className="abstract-rect abstract-rect-1"></div>
          <div className="abstract-rect abstract-rect-2"></div>
          <Gavel className="hero-bg-icon" size={160} />
        </div>
      </div>

      <div className="analysis-grid">
        <div className="analysis-main-col">
          {!result ? (
            <>
              <div className={`upload-card-premium ${file ? 'has-file' : ''}`}>
                <div className="upload-icon-wrapper">
                  <Upload size={32} />
                </div>
                <h3>Drag and drop your files here</h3>
                <p>or browse to find a file on your computer</p>

                <div className="upload-actions">
                  <input type="file" id="comp-upload" accept=".pdf" style={{ display: 'none' }}
                    onChange={e => pickFile(e.target.files[0])} />
                  <button className="btn-premium primary" onClick={() => document.getElementById('comp-upload').click()}>
                    Select Files
                  </button>
                </div>

                {file && (
                  <div className="file-preview-chip" style={{ marginBottom: 24 }}>
                    <FileText size={20} />
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <button className="remove-file" onClick={() => setFile(null)}>×</button>
                  </div>
                )}


                {loading && (
                  <div className="loading-overlay-glass">
                    <div className="processing-visual">
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring inner"></div>
                      <div className="processing-icon-center">
                        <Shield size={40} />
                      </div>
                    </div>
                    <div className="processing-status-text">{statusMessage || STAGES[stageIdx]}</div>
                    <div className="processing-subtext">Scanning for potential liabilities and risks</div>

                    <div className="progress-container-modern">
                      <div
                        className="progress-fill-modern"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>


              {!loading && file && !result && (
                <div style={{ marginTop: '32px' }}>
                  <button className="btn-premium primary" style={{ width: '100%', padding: '18px', fontSize: '16px', borderRadius: '16px' }} onClick={handleAnalyze}>
                    <Zap size={20} style={{ marginRight: '8px' }} />
                    Run Compliance Analysis
                  </button>
                </div>
              )}

              {!loading && !result && (
                <div className="capabilities-section" style={{ marginTop: '40px' }}>
                  <div className="section-divider" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ height: '1px', flex: 1, background: 'linear-gradient(to right, transparent, #e2e8f0)' }}></div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Legal Intelligence Capabilities</span>
                    <div style={{ height: '1px', flex: 1, background: 'linear-gradient(to left, transparent, #e2e8f0)' }}></div>
                  </div>

                  <div className="capabilities-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
                    <div className="cap-card" style={{ flex: '0 0 calc(50% - 8px)', background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: 'var(--color-primary)', marginBottom: '12px' }}><Shield size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Risk Pattern Detection</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Scans for unfavorable termination, unlimited liability, and hidden financial penalties.</p>
                    </div>
                    <div className="cap-card" style={{ flex: '0 0 calc(50% - 8px)', background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: '#27ae60', marginBottom: '12px' }}><CheckCircle size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Regulatory Cross-Ref</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Cross-references every clause against international standards and constitutional guidelines.</p>
                    </div>
                    <div className="cap-card" style={{ flex: '0 0 calc(50% - 8px)', background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: '#f39c12', marginBottom: '12px' }}><Sparkles size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>NLP Clause Extraction</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Uses advanced Legal-BERT models to accurately segment and classify complex legal language.</p>
                    </div>
                    {/* <div className="cap-card" style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: '#9b59b6', marginBottom: '12px' }}><Zap size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Instant Mitigation</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Provides suggested legal rewrites to fix non-compliant or high-risk contract sections.</p>
                    </div> */}
                  </div>
                </div>
              )}

            </>
          ) : (
            <div className="analysis-result-view">
              <PageHeader
                title={result.fileName}
                subtitle={`Risk Analysis completed on ${new Date().toLocaleDateString()}`}
                onBack={() => setResult(null)}
                actionButton={
                  <button className="btn-premium secondary" onClick={handleDownload}>
                    <Download size={18} /> Download Report
                  </button>
                }
              />

              <div style={{ display: 'flex', gap: 24, marginTop: 24 }}>
                <div style={{ flex: '0 0 200px' }}>
                  <ScoreRing score={result.complianceScore} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 12px 0' }}>Executive Summary</h3>
                  <p style={{ lineHeight: 1.6, color: 'var(--color-text-secondary)' }}>
                    {sanitizeSensitiveData(result.executiveSummary)}
                  </p>
                </div>
              </div>

              <div style={{ marginTop: 32 }}>
                <h3 style={{ marginBottom: 16 }}>Detected Risks & Clauses</h3>
                <div className="issues-list">
                  {result.clauses.map(c => <ClauseCard key={c.clause_index} clause={c} />)}
                </div>
              </div>

              {result.advocateRecs && result.advocateRecs.length > 0 && (
                <div style={{ marginTop: 40 }}>
                  <h3 style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Sparkles size={20} color="#6C3483" />
                    Recommended Legal Specialists
                  </h3>
                  <div className="advocate-recommendations-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 20
                  }}>
                    {result.advocateRecs.map((adv, idx) => (
                      <div key={idx} className="advocate-card-glass" style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '20px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 15 }}>
                          <h4 style={{ margin: 0, color: '#1a237e' }}>{adv.name}</h4>
                          <span style={{
                            background: '#e8f5e9',
                            color: '#2e7d32',
                            padding: '4px 8px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 700
                          }}>
                            {adv.score}% Match
                          </span>
                        </div>
                        <div style={{ marginBottom: 15 }}>
                          {adv.reasons && adv.reasons.map((reason, ri) => (
                            <div key={ri} style={{
                              fontSize: '13px',
                              color: '#64748b',
                              display: 'flex',
                              gap: 8,
                              marginBottom: 6,
                              lineHeight: 1.4
                            }}>
                              <CheckCircle size={14} style={{ flexShrink: 0, marginTop: 2, color: '#4caf50' }} />
                              {reason}
                            </div>
                          ))}
                        </div>
                        <button
                          className="btn-premium primary"
                          style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                          onClick={() => {
                            console.log("[ContractCompliance] Navigating to profile for:", adv.name, adv);
                            onNavigate('profiles', adv);
                          }}
                        >
                          View Full Profile
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="analysis-sidebar-col">
          <div className="sidebar-info-card">
            <div className="card-body-content">
              <div className="card-icon-header"><Shield size={20} /></div>
              <h4>Compliance AI</h4>
              <p>Our system audits contracts against the Constitution and local laws to identify potential risks.</p>
              <button
                className="btn-premium secondary"
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px' }}
                onClick={() => window.open('/compliance_guide.html', '_blank')}
              >
                Learn More <ExternalLink size={16} />
              </button>
            </div>
          </div>


          {/* System Status Removed */}
        </div>
      </div>
    </div>
  );
}
